import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Save, Check, Loader2, Calendar, Target, Users, TrendingUp, MessageSquare, PenLine } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { SignatureCanvas } from "./SignatureCanvas";
import { useConversationFieldSettings } from "@/hooks/useConversationFieldSettings";

interface EmployeeConversationFormProps {
  employeeId: string;
  employeeName: string;
  conversationId: string | null;
  onClose: () => void;
}

interface ConversationMetadata {
  conversation_type: "jahresgespraech" | "followup" | "sonstiges";
  conversation_date: string;
  follow_up_date: string | null;
  status: "entwurf" | "abgeschlossen";
  besondere_bemerkungen: string;
  zusammenfassung_erfolge: string;
  zusammenfassung_ziele: string;
  zusammenfassung_followup: string;
  supervisor_name: string;
  supervisor_signed_at: string | null;
  supervisor_signature_url: string | null;
  employee_signed_name: string;
  employee_signed_at: string | null;
  employee_signature_url: string | null;
}

const initialMetadata: ConversationMetadata = {
  conversation_type: "jahresgespraech",
  conversation_date: new Date().toISOString().split("T")[0],
  follow_up_date: null,
  status: "entwurf",
  besondere_bemerkungen: "",
  zusammenfassung_erfolge: "",
  zusammenfassung_ziele: "",
  zusammenfassung_followup: "",
  supervisor_name: "",
  supervisor_signed_at: null,
  supervisor_signature_url: null,
  employee_signed_name: "",
  employee_signed_at: null,
  employee_signature_url: null,
};

// Block icons mapping
const blockIcons: Record<string, React.ReactNode> = {
  block1: <TrendingUp className="h-5 w-5 text-primary" />,
  block2: <Target className="h-5 w-5 text-primary" />,
  block3: <Users className="h-5 w-5 text-primary" />,
  block4: <TrendingUp className="h-5 w-5 text-primary" />,
  block5: <Target className="h-5 w-5 text-primary" />,
};

export function EmployeeConversationForm({
  employeeId,
  employeeName,
  conversationId,
  onClose,
}: EmployeeConversationFormProps) {
  const { user, profile, userRole } = useAuth();
  const { blocks, loading: blocksLoading } = useConversationFieldSettings();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [metadata, setMetadata] = useState<ConversationMetadata>(initialMetadata);
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({});

  const isAdmin = userRole === "admin";

  useEffect(() => {
    if (conversationId) {
      loadConversation();
    }
  }, [conversationId]);

  const loadConversation = async () => {
    if (!conversationId) return;

    try {
      setLoading(true);
      const { data: conv, error } = await supabase
        .from("employee_conversations")
        .select("*")
        .eq("id", conversationId)
        .single();

      if (error) throw error;

      // Extract metadata
      setMetadata({
        conversation_type: conv.conversation_type,
        conversation_date: conv.conversation_date,
        follow_up_date: conv.follow_up_date,
        status: conv.status,
        besondere_bemerkungen: conv.besondere_bemerkungen || "",
        zusammenfassung_erfolge: conv.zusammenfassung_erfolge || "",
        zusammenfassung_ziele: conv.zusammenfassung_ziele || "",
        zusammenfassung_followup: conv.zusammenfassung_followup || "",
        supervisor_name: conv.supervisor_name || "",
        supervisor_signed_at: conv.supervisor_signed_at,
        supervisor_signature_url: conv.supervisor_signature_url,
        employee_signed_name: conv.employee_signed_name || "",
        employee_signed_at: conv.employee_signed_at,
        employee_signature_url: conv.employee_signature_url,
      });

      // Extract all dynamic field values from the conversation
      const dynamicFields: Record<string, string> = {};
      Object.keys(conv).forEach((key) => {
        if (key.startsWith("block") && conv[key]) {
          dynamicFields[key] = conv[key];
        }
      });
      setFieldValues(dynamicFields);
    } catch (error: any) {
      console.error("Error loading conversation:", error);
      toast.error("Fehler beim Laden des Gesprächs");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (finalize: boolean = false) => {
    try {
      setSaving(true);

      const saveData = {
        employee_id: employeeId,
        ...metadata,
        ...fieldValues,
        status: finalize ? "abgeschlossen" : metadata.status,
        created_by: user?.id,
      };

      if (conversationId) {
        const { error } = await supabase
          .from("employee_conversations")
          .update(saveData)
          .eq("id", conversationId);

        if (error) throw error;
        toast.success(finalize ? "Gespräch abgeschlossen" : "Gespräch gespeichert");
      } else {
        const { error } = await supabase
          .from("employee_conversations")
          .insert([saveData]);

        if (error) throw error;
        toast.success("Gespräch erfolgreich angelegt");
      }

      onClose();
    } catch (error: any) {
      console.error("Error saving conversation:", error);
      toast.error("Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  const handleSupervisorSignature = async (signatureDataUrl: string) => {
    try {
      const fileName = `conversations/${employeeId}/${Date.now()}_supervisor.png`;
      const base64Data = signatureDataUrl.replace(/^data:image\/\w+;base64,/, "");
      const { error: uploadError } = await supabase.storage
        .from("employee-profiles")
        .upload(fileName, decode(base64Data), {
          contentType: "image/png",
          upsert: true,
        });

      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage
        .from("employee-profiles")
        .getPublicUrl(fileName);

      setMetadata({
        ...metadata,
        supervisor_signature_url: urlData.publicUrl,
        supervisor_signed_at: new Date().toISOString(),
        supervisor_name: profile?.full_name || "Vorgesetzter",
      });

      toast.success("Unterschrift gespeichert");
    } catch (error: any) {
      console.error("Error saving signature:", error);
      toast.error("Fehler beim Speichern der Unterschrift");
    }
  };

  const handleEmployeeSignature = async (signatureDataUrl: string) => {
    try {
      const fileName = `conversations/${employeeId}/${Date.now()}_employee.png`;
      const base64Data = signatureDataUrl.replace(/^data:image\/\w+;base64,/, "");
      const { error: uploadError } = await supabase.storage
        .from("employee-profiles")
        .upload(fileName, decode(base64Data), {
          contentType: "image/png",
          upsert: true,
        });

      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage
        .from("employee-profiles")
        .getPublicUrl(fileName);

      setMetadata({
        ...metadata,
        employee_signature_url: urlData.publicUrl,
        employee_signed_at: new Date().toISOString(),
        employee_signed_name: employeeName,
      });

      toast.success("Unterschrift gespeichert");
    } catch (error: any) {
      console.error("Error saving signature:", error);
      toast.error("Fehler beim Speichern der Unterschrift");
    }
  };

  const updateMetadataField = (field: keyof ConversationMetadata, value: any) => {
    setMetadata({ ...metadata, [field]: value });
  };

  const updateFieldValue = (key: string, value: string) => {
    setFieldValues({ ...fieldValues, [key]: value });
  };

  if (loading || blocksLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Metadata Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Gesprächsdaten
          </CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label>Mitarbeiter</Label>
            <Input value={employeeName} disabled />
          </div>
          <div className="space-y-2">
            <Label>Gesprächsart</Label>
            <Select
              value={metadata.conversation_type}
              onValueChange={(value) => updateMetadataField("conversation_type", value)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="jahresgespraech">Jahresgespräch</SelectItem>
                <SelectItem value="followup">Follow-up-Gespräch</SelectItem>
                <SelectItem value="sonstiges">Sonstiges</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Gesprächsdatum</Label>
            <Input
              type="date"
              value={metadata.conversation_date}
              onChange={(e) => updateMetadataField("conversation_date", e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label>Follow-up-Termin (optional)</Label>
            <Input
              type="date"
              value={metadata.follow_up_date || ""}
              onChange={(e) => updateMetadataField("follow_up_date", e.target.value || null)}
            />
          </div>
          <div className="space-y-2">
            <Label>Status</Label>
            <Badge variant={metadata.status === "abgeschlossen" ? "default" : "secondary"}>
              {metadata.status === "abgeschlossen" ? "Abgeschlossen" : "Entwurf"}
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* Dynamic Content Blocks from Settings */}
      <Accordion 
        type="multiple" 
        defaultValue={blocks.map((b) => b.block_id)} 
        className="space-y-4"
      >
        {blocks.map((block) => (
          <AccordionItem 
            key={block.block_id} 
            value={block.block_id} 
            className="border rounded-lg px-4"
          >
            <AccordionTrigger className="hover:no-underline">
              <div className="flex items-center gap-2">
                {blockIcons[block.block_id] || <MessageSquare className="h-5 w-5 text-primary" />}
                <span className="font-semibold">{block.block_label}</span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="space-y-4 pt-4">
              {block.fields.map((field) => (
                <div key={field.key} className="space-y-2">
                  <Label>{field.label}</Label>
                  <Textarea
                    value={fieldValues[field.key] || ""}
                    onChange={(e) => updateFieldValue(field.key, e.target.value)}
                    placeholder={field.placeholder}
                    rows={3}
                  />
                </div>
              ))}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>

      {/* Besondere Bemerkungen */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5" />
            Besondere Bemerkungen / Sonstiges
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Textarea
            value={metadata.besondere_bemerkungen}
            onChange={(e) => updateMetadataField("besondere_bemerkungen", e.target.value)}
            placeholder="Freies Textfeld für zusätzliche Anmerkungen..."
            rows={4}
          />
        </CardContent>
      </Card>


      {/* Unterschriften */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <PenLine className="h-5 w-5" />
            Unterschriften
          </CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <SignatureCanvas
              onSave={handleSupervisorSignature}
              existingSignature={metadata.supervisor_signature_url}
              signedAt={metadata.supervisor_signed_at}
              signedByName={metadata.supervisor_name}
              title="Unterschrift Vorgesetzter"
              description="Unterschrift des Vorgesetzten"
              confirmationText="Mit Ihrer Unterschrift bestätigen Sie das Gespräch als Vorgesetzter."
              isAdmin={isAdmin}
            />
          </div>
          <div>
            <SignatureCanvas
              onSave={handleEmployeeSignature}
              existingSignature={metadata.employee_signature_url}
              signedAt={metadata.employee_signed_at}
              signedByName={metadata.employee_signed_name}
              title="Unterschrift Mitarbeiter"
              description="Unterschrift des Mitarbeiters"
              confirmationText="Mit Ihrer Unterschrift bestätigen Sie die Kenntnisnahme des Gesprächs."
              isAdmin={isAdmin}
            />
          </div>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <div className="flex items-center justify-end gap-4 pt-4">
        <Button variant="outline" onClick={onClose}>
          Abbrechen
        </Button>
        <Button variant="secondary" onClick={() => handleSave(false)} disabled={saving}>
          {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
          Als Entwurf speichern
        </Button>
        <Button onClick={() => handleSave(true)} disabled={saving}>
          {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Check className="h-4 w-4 mr-2" />}
          Gespräch abschließen
        </Button>
      </div>
    </div>
  );
}

// Helper function to decode base64
function decode(base64: string): Uint8Array {
  const binaryString = atob(base64);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes;
}
